package com.crowpay.views.components

import com.crowpay.Specification
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.extensions.toFileData
import com.crowpay.extensions.withSpacing
import com.crowpay.sdk.currentSession
import com.crowpay.sdk.notNullSession
import com.crowpay.trash
import com.crowpay.utils.Action
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.pushChanges
import com.crowpay.utils.renderDollars
import com.crowpay.utils.resize
import com.crowpay.utils.truncate
import com.crowpay.utils.validation.Validator
import com.crowpay.views.components.files.FilePreview
import com.crowpay.views.components.files.resolveProjectAttachments
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.nav.viewIsWide
import com.crowpay.views.dialogs.GenericConfirmationDialog
import com.crowpay.views.theming.DangerButtonSemantic
import com.crowpay.views.theming.SectionSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.deleteButton
import com.crowpay.views.theming.expandButtonTheme
import com.crowpay.views.theming.primaryButton
import com.crowpay.views.theming.smallBody
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.textButton
import com.lightningkite.UUID
import com.lightningkite.kiteui.ViewWrapper
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Screen
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.render
import com.lightningkite.kiteui.reactive.Draft
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.await
import com.lightningkite.kiteui.reactive.awaitOnce
import com.lightningkite.kiteui.reactive.debounce
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.modify
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atEnd
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.gravity
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.rowCollapsingToColumn
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.italic
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningserver.files.ServerFile

class EditSpecificationItem(
    ogSpec: Specification?,
    contractorId: UUID,
    val onRemove: suspend ViewWriter.(UUID) -> Unit,
    val onSave: suspend (Specification) -> Unit = {}
) : Screen {
    private val spec = ogSpec ?: Specification(contractor = contractorId)
    val id = spec._id
    var savedToDB = Property(ogSpec != null)
    val draft = Draft(spec)
    val files = Property(ogSpec?.files?.toFileData() ?: emptyList())
    val expanded = Property(false)
    val expandedDebounced = shared {
        expanded.debounce(200)()
    }
    val form = SpecificationItemForm(draft, files)
    val validator: Validator get() = form

    val save = Action("Save") {
        val session = currentSession() ?: return@Action

        onSave(draft())

        if (savedToDB.value) {
            val filesChanged =
                files.value.size != draft.awaitOnce().files.size ||
                        files.value.any {
                            it.preview is FilePreview.LocalFile ||
                                    it.name.value != (it.original as? FilePreview.RemoteAttachment)?.attachment?.name
                        }
            if (!draft.changesMade.awaitOnce() && !filesChanged) return@Action
            val current = draft.awaitOnce()

            val newFiles = files.awaitOnce().resolveProjectAttachments()
            val newFilesSimplified = newFiles.map {
                it.copy(
                    file = ServerFile(it.file.location.substringBefore("?")),
                    preview = it.preview?.let {
                        ServerFile(
                            it.location.substringBefore("?")
                        )
                    }
                )
            }
            val oldFiles = current.files.map {
                it.copy(
                    file = ServerFile(it.file.location.substringBefore('?')),
                    preview = it.preview?.let {
                        ServerFile(
                            it.location.substringBefore('?')
                        )
                    }
                )
            }
            if (oldFiles != newFilesSimplified) {
                draft.modify {
                    it.copy(files = newFiles)
                }
            }
            val result = draft.pushChanges(notNullSession().specifications)
            files.set(result.files.toFileData())
        } else {
            println("Inserting Specification")
            val current = draft.awaitOnce()
            val newFiles = files.awaitOnce().resolveProjectAttachments()

            val result = session.specifications.insert(
                current.copy(files = newFiles)
            ).await()!!
            draft.published.set(result)
            draft.cancel()
            files.set(result.files.toFileData())
            savedToDB.set(true)
        }
        expanded.set(false)
    }

    suspend fun ViewWriter.remove() {
        val (header, removeMessage) = if (savedToDB.await()) {
            "Delete Specification?" to "Deleting a specification cannot be undone. Are you sure you want to delete this Specification?"
        } else {
            "Discard Specification?" to "Discarding unsaved work cannot be undone. Are you sure you want to discard this unsaved Specification?"
        }
        dialogScreenNavigator.navigate(
            GenericConfirmationDialog(
                header = header,
                message = removeMessage,
                question = null,
                confirmationText = header.trim('?'),
                declineText = "Go Back",
                confirmationTheme = DangerButtonSemantic,
                onSubmit = { confirmed ->
                    if (confirmed) {
                        val id = draft()._id
                        if (savedToDB.await())
                            notNullSession().nonCached.specification.delete(id)
                        onRemove(id)
                    }
                }
            )
        )
    }


    override fun ViewWriter.render() {
        col {
            spacing = 0.rem
            withSpacing(0.rem) - row {
                dynamicTheme {
                    if (expanded()) SectionSemantic.WithoutPadding else null
                }
                expanding - expandButtonTheme - button {
                    spacing = 0.rem
                    row {
                        centered - expandIcon(expanded)
                        (if (viewIsNarrow) expanding else ViewWrapper) - centered - body {
                            ::content { form.specTitle().ifBlank { "New Specification" } }
                        }
                        if (viewIsWide) expanding - centered - italic - smallBody {
                            ::content {
                                form.description().truncate(50)
                            }
                        }
                        centered - smallBody {
                            existsDefaultFalse { form.price() > 0 }
                            ::content { form.price().renderDollars() }
                        }
                    }
                    action = expandAction(expanded)
                }
                centered - onlyWhen { savedToDB() } - deleteButton - button {
                    icon(Icon.trash.resize(1.2.rem), "Remove Specification")
                    onClick { remove() }
                }
            }
            onlyWhen { expanded() } - indentedCol(Indent.CenteredSection) {
                space()
                form.render(this)
                atEnd - row {
                    spacing = AppDimensions.buttonRowSpacing
                    textButton - button {
                        specCenteredText("Discard")
                        onClick {
                            if (savedToDB.await()) {
                                draft.cancel()
                                files.set(draft.awaitOnce().files.toFileData())
                                expanded.set(false)
                            } else {
                                remove()
                            }
                        }
                    }
                    primaryButton - gravity(Align.End, Align.Center) - button {
                        ::enabled { validator.allValid() }
                        specCenteredText("Save")
                        action = save
                    }
                }
                space()
            }
        }
    }
}